import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  FileDownload as ExcelIcon,
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { Unit } from '../../types/units.types';
import unitsService from '../../services/units.service';
import UnitDialog from '../../components/units/UnitDialog';
import { useLanguage } from '../../contexts/LanguageContext';
import { useConfirm } from '../../hooks/useConfirm';
import { toast } from 'react-toastify';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import config from '../../config';

const UnitsPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const [units, setUnits] = useState<Unit[]>([]);
  const [filteredUnits, setFilteredUnits] = useState<Unit[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    loadUnits();
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
  }, []);

  useEffect(() => {
    const filtered = !searchTerm
      ? units
      : units.filter(
          (unit) =>
            unit.unitCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
            unit.unitName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            unit.unitNameAr.includes(searchTerm)
        );
    setFilteredUnits(filtered);
  }, [searchTerm, units]);

  const loadUnits = async () => {
    setLoading(true);
    try {
      const data = await unitsService.getAll();
      setUnits(data);
      setFilteredUnits(data);
    } catch (error) {
      console.error('Error loading units:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedUnit(null);
    setDialogOpen(true);
  };

  const handleEdit = (unit: Unit) => {
    setSelectedUnit(unit);
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ message: isRTL ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?', severity: 'error' });
    if (!ok) return;
    try {
      await unitsService.delete(id);
      loadUnits();
    } catch (error) {
      toast.error(isRTL ? 'فشل حذف الوحدة' : 'Failed to delete unit');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUnit(null);
  };

  const handlePrintList = () => {
    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:#1976d2;margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const rows = filteredUnits.map(u => `
      <tr>
        <td>${u.unitCode}</td>
        <td>${u.unitName}</td>
        <td>${u.unitNameAr}</td>
        <td>${u.isBaseUnit ? (isRTL ? 'أساسية' : 'Base') : '-'}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head>
      <style>
        body{font-family:${isRTL ? 'Cairo,' : ''}Arial,sans-serif;margin:20px;font-size:12px;color:#333;}
        h1{text-align:center;color:#1976d2;margin:10px 0 15px;font-size:18px;}
        table{width:100%;border-collapse:collapse;margin-top:10px;}
        th,td{border:1px solid #ddd;padding:6px 8px;font-size:11px;}
        th{background:#1976d2;color:white;font-weight:bold;}
        tr:nth-child(even){background:#f9f9f9;}
        @page{size:A4;margin:1cm;}
      </style></head><body>
      ${companyHeader}
      <h1>${isRTL ? 'إدارة الوحدات' : 'Units Management'}</h1>
      <table>
        <thead><tr>
          <th>${isRTL ? 'الكود' : 'Code'}</th>
          <th>${isRTL ? 'الاسم بالإنجليزية' : 'Name (EN)'}</th>
          <th>${isRTL ? 'الاسم بالعربية' : 'Name (AR)'}</th>
          <th>${isRTL ? 'وحدة أساسية' : 'Base Unit'}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="margin-top:10px;font-size:11px;color:#999;">Printed: ${new Date().toLocaleString()}</p>
    </body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 500); }
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(isRTL ? 'الوحدات' : 'Units');

      let currentRow = 1;

      // Company Logo
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
          worksheet.addImage(logoImage, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 60 } });
        } catch {}
      }

      // Company Header
      worksheet.mergeCells('B1:D1');
      const companyCell = worksheet.getCell('B1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 18, bold: true, color: { argb: 'FF5E35B1' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('B2:D2');
      const addressCell = worksheet.getCell('B2');
      addressCell.value = companyInfo?.address || '';
      addressCell.font = { size: 9, color: { argb: 'FF424242' } };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('B3:D3');
      const contactCell = worksheet.getCell('B3');
      contactCell.value = `${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9, color: { argb: 'FF424242' } };
      contactCell.alignment = { horizontal: 'center' };

      currentRow = 5;

      // Report Title
      worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
      const titleCell = worksheet.getCell(`A${currentRow}`);
      titleCell.value = isRTL ? 'إدارة الوحدات' : 'Units Management';
      titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5E35B1' } };
      worksheet.getRow(currentRow).height = 30;
      currentRow++;

      // Generated date
      worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
      const dateCell = worksheet.getCell(`A${currentRow}`);
      dateCell.value = `${isRTL ? 'تاريخ التصدير' : 'Generated on'}: ${new Date().toLocaleDateString('en-GB')} | ${isRTL ? 'الإجمالي' : 'Total'}: ${filteredUnits.length}`;
      dateCell.font = { size: 10, color: { argb: 'FF5E35B1' } };
      dateCell.alignment = { horizontal: 'center' };
      currentRow += 2;

      // Headers
      const headers = [
        isRTL ? 'الكود' : 'Code',
        isRTL ? 'الاسم بالإنجليزية' : 'Name (EN)',
        isRTL ? 'الاسم بالعربية' : 'Name (AR)',
        isRTL ? 'وحدة أساسية' : 'Base Unit',
      ];
      const headerRow = worksheet.getRow(currentRow);
      headerRow.values = headers;
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 9 };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5E35B1' } };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.height = 25;
      currentRow++;

      // Data rows
      filteredUnits.forEach((unit, idx) => {
        const row = worksheet.getRow(currentRow);
        row.values = [
          unit.unitCode,
          unit.unitName,
          unit.unitNameAr,
          unit.isBaseUnit ? (isRTL ? 'أساسية' : 'Base') : '-',
        ];
        row.alignment = { horizontal: 'center', vertical: 'middle' };
        row.height = 20;
        if (idx % 2 === 0) {
          row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAF8FF' } };
        }
        currentRow++;
      });

      // Column widths
      worksheet.getColumn(1).width = 15;
      worksheet.getColumn(2).width = 30;
      worksheet.getColumn(3).width = 30;
      worksheet.getColumn(4).width = 15;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Units_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Exported successfully');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error(isRTL ? 'فشل التصدير' : 'Excel export failed');
    }
  };

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          {language === 'ar' ? 'إدارة الوحدات' : 'Units Management'}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title={isRTL ? 'طباعة' : 'Print'}>
            <IconButton onClick={handlePrintList} color="primary"><PrintIcon /></IconButton>
          </Tooltip>
          <Tooltip title={isRTL ? 'تصدير Excel' : 'Export Excel'}>
            <IconButton onClick={exportToExcel} color="success"><ExcelIcon /></IconButton>
          </Tooltip>
          <Tooltip title={isRTL ? 'تحديث' : 'Refresh'}>
            <IconButton onClick={loadUnits} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            {language === 'ar' ? 'إضافة وحدة' : 'Add Unit'}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder={language === 'ar' ? 'البحث...' : 'Search...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell><strong>{language === 'ar' ? 'الكود' : 'Code'}</strong></TableCell>
                <TableCell><strong>{language === 'ar' ? 'الاسم بالإنجليزية' : 'Name (EN)'}</strong></TableCell>
                <TableCell><strong>{language === 'ar' ? 'الاسم بالعربية' : 'Name (AR)'}</strong></TableCell>
                <TableCell align="center"><strong>{language === 'ar' ? 'وحدة أساسية' : 'Base Unit'}</strong></TableCell>
                <TableCell align="center"><strong>{language === 'ar' ? 'الإجراءات' : 'Actions'}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
                  </TableCell>
                </TableRow>
              ) : filteredUnits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {language === 'ar' ? 'لا توجد وحدات' : 'No units found'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUnits.map((unit) => (
                  <TableRow key={unit.unitId} hover>
                    <TableCell>{unit.unitCode}</TableCell>
                    <TableCell>{unit.unitName}</TableCell>
                    <TableCell>{unit.unitNameAr}</TableCell>
                    <TableCell align="center">
                      {unit.isBaseUnit && (
                        <Chip label={language === 'ar' ? 'أساسية' : 'Base'} color="primary" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" size="small" onClick={() => handleEdit(unit)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton color="error" size="small" onClick={() => handleDelete(unit.unitId)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {language === 'ar' ? `إجمالي: ${filteredUnits.length}` : `Total: ${filteredUnits.length}`}
          </Typography>
        </Box>
      </Paper>

      <UnitDialog open={dialogOpen} onClose={handleDialogClose} onSuccess={loadUnits} unit={selectedUnit} />
    </Box>
  );
};

export default UnitsPage;